import { useEffect, useState } from 'react';
import { Check } from 'shared/icons';
import api from '../../services/api';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Modal from '../ui/Modal';
import Select from '../ui/Select';

function parseApiError(error, fallbackMessage) {
    return error?.response?.data?.error?.message || error?.response?.data?.message || error?.message || fallbackMessage;
}

const UserModal = ({ isOpen, onClose, onSuccess, user = null, permissionsCatalog = [] }) => {
    const [formData, setFormData] = useState({
        organization_id: '',
        full_name: '',
        email: '',
        password: '',
        selectedPermissions: [],
    });
    const [organizations, setOrganizations] = useState([]);
    const [organizationsLoading, setOrganizationsLoading] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        if (user) {
            const catalogKeys = new Set(
                (permissionsCatalog || [])
                    .map((permission) => String(permission?.permission_key || '').trim())
                    .filter(Boolean),
            );
            const permissionKeys = Array.isArray(user.permissions)
                ? user.permissions.filter((value) => typeof value === 'string' && catalogKeys.has(value))
                : [];

            setFormData({
                organization_id: user.organization_id || '',
                full_name: user.full_name || '',
                email: user.email || '',
                password: '',
                selectedPermissions: permissionKeys,
            });
        } else {
            setFormData({
                organization_id: '',
                full_name: '',
                email: '',
                password: '',
                selectedPermissions: [],
            });
        }

        setError('');
    }, [user, isOpen, permissionsCatalog]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        let isMounted = true;
        setOrganizationsLoading(true);
        api.getOrganizations({ include_inactive: 'true' })
            .then((response) => {
                if (!isMounted) {
                    return;
                }
                const results = Array.isArray(response?.results) ? response.results : [];
                setOrganizations(results.filter((entry) => Boolean(entry?.id)));
            })
            .catch(() => {
                if (!isMounted) {
                    return;
                }
                setOrganizations([]);
            })
            .finally(() => {
                if (isMounted) {
                    setOrganizationsLoading(false);
                }
            });

        return () => {
            isMounted = false;
        };
    }, [isOpen]);

    const handleSubmit = async (event) => {
        event.preventDefault();
        setError('');
        setLoading(true);

        try {
            if (user) {
                await api.updateUser(user.id, {
                    full_name: formData.full_name,
                    email: formData.email,
                    permission_keys: formData.selectedPermissions,
                    ...(formData.password.trim() ? { password: formData.password } : {}),
                });
            } else {
                if (!formData.password.trim()) {
                    setError('Senha e obrigatoria para novo usuario');
                    setLoading(false);
                    return;
                }

                if (!formData.organization_id) {
                    setError('Selecione uma organização para o usuário');
                    setLoading(false);
                    return;
                }

                await api.createUser({
                    organization_id: formData.organization_id,
                    full_name: formData.full_name,
                    email: formData.email,
                    password: formData.password,
                    permission_keys: formData.selectedPermissions,
                });
            }

            onSuccess();
            onClose();
        } catch (submitError) {
            setError(parseApiError(submitError, 'Erro ao salvar usuario'));
        } finally {
            setLoading(false);
        }
    };

    const togglePermission = (permissionKey) => {
        setFormData((previous) => ({
            ...previous,
            selectedPermissions: previous.selectedPermissions.includes(permissionKey)
                ? previous.selectedPermissions.filter((key) => key !== permissionKey)
                : [...previous.selectedPermissions, permissionKey],
        }));
    };

    if (!isOpen) {
        return null;
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={user ? 'Editar usuario' : 'Novo usuario'}
            size="lg"
            contentClassName="max-h-[90vh] overflow-y-auto"
        >
            <form onSubmit={handleSubmit} className="space-y-4">
                <Input
                    label="Nome completo"
                    value={formData.full_name}
                    onChange={(event) => setFormData({ ...formData, full_name: event.target.value })}
                    required
                    placeholder="Joao Silva"
                />

                <Input
                    label="E-mail"
                    type="email"
                    value={formData.email}
                    onChange={(event) => setFormData({ ...formData, email: event.target.value })}
                    required
                    placeholder="joao@example.com"
                />

                {!user ? (
                    <Select
                        label="Organização"
                        value={formData.organization_id}
                        onChange={(event) => setFormData({ ...formData, organization_id: event.target.value })}
                        disabled={organizationsLoading}
                        required
                    >
                        <option value="">Selecione uma organização</option>
                        {organizations.map((organization) => (
                            <option key={organization.id} value={organization.id}>
                                {organization.name} ({organization.type})
                            </option>
                        ))}
                    </Select>
                ) : null}

                <Input
                    label={user ? 'Nova senha (deixe em branco para manter)' : 'Senha'}
                    type="password"
                    value={formData.password}
                    onChange={(event) => setFormData({ ...formData, password: event.target.value })}
                    required={!user}
                    placeholder="********"
                />

                <div>
                    <p className="mb-2 block text-sm font-medium text-mono-700 dark:text-mono-300">Permissoes</p>
                    <div className="space-y-2">
                        {permissionsCatalog.map((permission) => (
                            <label
                                key={permission.permission_key}
                                className="flex cursor-pointer items-center rounded-lg border border-mono-200 p-3 transition-colors hover:bg-mono-50 dark:border-mono-800 dark:hover:bg-mono-900"
                            >
                                <input
                                    type="checkbox"
                                    checked={formData.selectedPermissions.includes(permission.permission_key)}
                                    onChange={() => togglePermission(permission.permission_key)}
                                    className="mr-3 h-4 w-4"
                                />
                                <div>
                                    <div className="font-medium text-black dark:text-white">
                                        {permission.permission_key}
                                    </div>
                                    <div className="text-xs text-mono-600 dark:text-mono-400">
                                        {permission.description}
                                    </div>
                                </div>
                            </label>
                        ))}
                    </div>
                </div>

                {error ? (
                    <div className="rounded-lg border border-mono-300 bg-mono-100 p-3 dark:border-mono-700 dark:bg-mono-900">
                        <p className="text-sm text-mono-800 dark:text-mono-300">{error}</p>
                    </div>
                ) : null}

                <div className="flex flex-col gap-3 pt-4 sm:flex-row">
                    <Button type="submit" loading={loading} className="flex-1">
                        <Check size={20} className="mr-2" />
                        {user ? 'Salvar alteracoes' : 'Criar usuario'}
                    </Button>
                    <Button type="button" onClick={onClose} variant="secondary" className="sm:w-auto">
                        Cancelar
                    </Button>
                </div>
            </form>
        </Modal>
    );
};

export default UserModal;
